import { useState } from 'react';
import { AppDispatch } from 'redux/store';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Container, Divider, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import TextInput from 'components/Common/TextInput';
import ChangePhoto from 'pages/Account/ChangePhoto';
import Button from 'components/Common/Button';
import {
    createAnnouncementGroup,
    fetchAnnouncementGroups,
    uploadAnnouncementGroupImage,
} from 'redux/slices/announcements/announcementGroupSlice';
import ErrorMessage from 'components/Common/ErrorMessage';
import Routes from 'constants/routes';
import InfoModal from 'components/Common/InfoModal';
import CardWrapper from 'components/Common/CardWrapper';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';

const defaultValues = {
    title: '',
    contact: '',
};
const formSchema = yup
    .object({
        title: yup.string().required('Enter Group Title. ').min(3, 'Title is too short - should be 3 chars minimum.'),
        contact: yup.string().min(0).max(2000),
    })
    .required();

const TITLE = 'Community Group';
const DESCRIPTION =
    'To create a group to easily send announcements to a specific group of parishioners please provide details here. Once created you can invite member by accessing the group in your Announcements tab within this portal. ';

const CreateAnnouncementGroup = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageAnnouncements = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');
    const [loading, setLoading] = useState(false);
    const [imgValue, setImgValue] = useState<string | Blob>('');
    const [modalOpen, setModalOpen] = useState(false);
    const [createGroupId, setCreateGroupId] = useState('');
    const [imageUploadFailed, setImageUploadFailed] = useState(false);
    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues,
    });

    const openSuccess = (groupId: string) => {
        setCreateGroupId(groupId);
        setImageUploadFailed(false);
        setModalOpen(true);
        if (parishId) {
            dispatch(fetchAnnouncementGroups({ parishId }));
        }
    };

    const onFormSubmit = async (formData: { title: string; contact?: string }) => {
        if (!parishId || !canManageAnnouncements) return;
        try {
            setLoading(true);

            // H2: metadata already created — retry image PUT only (never re-POST).
            if (createGroupId && imageUploadFailed) {
                if (!(imgValue instanceof Blob)) {
                    setLoading(false);
                    return;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing dispatched thunk payload union
                const { payload }: any = await dispatch(
                    uploadAnnouncementGroupImage({
                        parishId,
                        groupId: createGroupId,
                        image: imgValue,
                    })
                );
                setLoading(false);
                if (payload?.group?.id) {
                    openSuccess(payload.group.id);
                }
                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing dispatched thunk payload union
            const { payload }: any = await dispatch(
                createAnnouncementGroup({
                    parishId,
                    payload: {
                        title: formData.title,
                        ...(formData.contact?.trim() ? { description: formData.contact.trim() } : {}),
                    },
                    image: imgValue instanceof Blob ? imgValue : null,
                })
            );
            setLoading(false);
            if (!payload?.group?.id) return;

            setCreateGroupId(payload.group.id);
            if (payload.imageFailed) {
                setImageUploadFailed(true);
                return;
            }
            openSuccess(payload.group.id);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- pre-existing: caught but never inspected
        } catch (error) {
            setLoading(false);
        }
    };

    const handleGroupsList = () => {
        navigate(Routes.announcements.main, { replace: true });
    };
    const handleGroupDetail = () => {
        navigate(Routes.announcements.groupDetails.replace(':id', createGroupId), { replace: true });
    };

    if (!canManageAnnouncements) {
        return (
            <CardWrapper>
                <Alert severity="error">You need Merchant Admin or higher to create a group.</Alert>
            </CardWrapper>
        );
    }

    return (
        <CardWrapper>
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Container sx={{ py: '2rem' }}>
                    <Typography sx={{ fontSize: 18, mb: 2, fontWeight: 400 }} variant="h1">
                        {TITLE}
                    </Typography>
                    <Divider />
                    <Typography sx={{ mt: 2, mb: 4, fontSize: 16, fontWeight: 400 }} variant="body1">
                        {DESCRIPTION}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', marginBottom: '2rem' }}>
                        <Controller
                            name="title"
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    icon={null}
                                    label="Title"
                                    placeholder="Type title here"
                                    error={!!errors.title}
                                    helperText={errors.title?.message}
                                    disabled={!!createGroupId}
                                    sx={{ width: { xs: '100%', sm: '70%' } }}
                                />
                            )}
                        />
                        <Controller
                            name="contact"
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    icon={null}
                                    label="Description"
                                    placeholder="Type description here (optional)"
                                    error={!!errors.contact}
                                    helperText={errors.contact?.message}
                                    disabled={!!createGroupId}
                                    sx={{ width: { xs: '100%', sm: '70%' }, my: 4 }}
                                />
                            )}
                        />
                    </div>

                    <ChangePhoto imgValue={imgValue as string} setImgValue={setImgValue} />
                    {imageUploadFailed ? (
                        <ErrorMessage message="Group was created, but image upload failed. Choose an image and retry upload — the group will not be created again." />
                    ) : null}
                    <Stack alignItems="center" mt={10}>
                        <Button
                            loading={loading}
                            title={imageUploadFailed ? 'Retry Image Upload' : 'Create'}
                            type="submit"
                            sx={{ width: { xs: '100%', sm: '70%', md: '30%' } }}
                        />
                    </Stack>
                </Container>
            </form>

            <InfoModal
                description="Where do you want to navigate?"
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Create Announcement Group"
                renderActions={() => {
                    return (
                        <Stack direction={'row'} justifyContent={'space-around'}>
                            <Button onClick={handleGroupsList} title="Groups List" />
                            <Button onClick={handleGroupDetail} title="Group Detail" />
                        </Stack>
                    );
                }}
            />
        </CardWrapper>
    );
};

export default CreateAnnouncementGroup;
